import React, { useEffect, useRef } from "react";

/**
 * Decorative node-and-link field used behind the work section.
 *
 * Kept because the connected-node motif is part of the DepthLogix look,
 * but reworked so it behaves:
 *
 * - The animation frame is now cancelled on unmount. It previously
 *   self-scheduled forever, so every visit to the homepage left another
 *   loop running for the lifetime of the tab.
 * - pointer-events is off. The canvas is a full-size absolutely
 *   positioned overlay, and with pointer events on it was swallowing
 *   clicks on the carousel controls underneath it.
 * - It pauses when scrolled out of view and when the tab is hidden.
 * - Node count and link radius scale with the canvas, and the O(n²)
 *   neighbour pass is capped, so a large monitor does not cost more than
 *   a phone.
 * - Honours prefers-reduced-motion by drawing a single static frame.
 * - Renders at device pixel ratio (capped at 2) so it is not blurry on
 *   high-density screens.
 */
export default function BackgroundCanvas({ className = "" }) {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !canvas.parentElement) return undefined;

    const ctx = canvas.getContext("2d");
    if (!ctx) return undefined;

    const prefersReduced =
      window.matchMedia &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    let width = 0;
    let height = 0;
    let dpr = 1;
    let nodes = [];
    let frameId = null;
    let inView = true;

    const LINK_DISTANCE = 130;

    const buildNodes = () => {
      // Roughly one node per 22,000 css px², clamped to a sane range so
      // the cost is stable from a 360px phone to an ultrawide monitor.
      const target = Math.round((width * height) / 22000);
      const count = Math.max(18, Math.min(60, target));

      nodes = Array.from({ length: count }, () => ({
        x: Math.random() * width,
        y: Math.random() * height,
        vx: (Math.random() - 0.5) * 0.45,
        vy: (Math.random() - 0.5) * 0.45,
      }));
    };

    const resize = () => {
      const rect = canvas.parentElement.getBoundingClientRect();
      if (!rect.width || !rect.height) return;

      dpr = Math.min(window.devicePixelRatio || 1, 2);
      width = rect.width;
      height = rect.height;

      canvas.width = Math.round(width * dpr);
      canvas.height = Math.round(height * dpr);
      canvas.style.width = `${width}px`;
      canvas.style.height = `${height}px`;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

      buildNodes();
    };

    const render = (advance) => {
      ctx.clearRect(0, 0, width, height);

      if (advance) {
        for (const node of nodes) {
          node.x += node.vx;
          node.y += node.vy;
          if (node.x <= 0 || node.x >= width) node.vx *= -1;
          if (node.y <= 0 || node.y >= height) node.vy *= -1;
        }
      }

      // Links first so the dots sit on top.
      ctx.lineWidth = 1;
      for (let i = 0; i < nodes.length; i++) {
        for (let j = i + 1; j < nodes.length; j++) {
          const dx = nodes[i].x - nodes[j].x;
          const dy = nodes[i].y - nodes[j].y;
          const distSq = dx * dx + dy * dy;
          if (distSq > LINK_DISTANCE * LINK_DISTANCE) continue;

          const dist = Math.sqrt(distSq);
          ctx.strokeStyle = `rgba(26, 110, 216, ${(1 - dist / LINK_DISTANCE) * 0.28})`;
          ctx.beginPath();
          ctx.moveTo(nodes[i].x, nodes[i].y);
          ctx.lineTo(nodes[j].x, nodes[j].y);
          ctx.stroke();
        }
      }

      ctx.fillStyle = "rgba(56, 189, 248, 0.55)";
      for (const node of nodes) {
        ctx.beginPath();
        ctx.arc(node.x, node.y, 1.6, 0, Math.PI * 2);
        ctx.fill();
      }
    };

    const loop = () => {
      render(true);
      frameId = window.requestAnimationFrame(loop);
    };

    const start = () => {
      if (frameId !== null || prefersReduced) return;
      frameId = window.requestAnimationFrame(loop);
    };

    const stop = () => {
      if (frameId === null) return;
      window.cancelAnimationFrame(frameId);
      frameId = null;
    };

    resize();
    if (prefersReduced) {
      render(false);
    } else {
      start();
    }

    // Only animate while the section is actually on screen.
    let observer = null;
    if (typeof IntersectionObserver !== "undefined") {
      observer = new IntersectionObserver(
        ([entry]) => {
          inView = entry.isIntersecting;
          if (inView && !document.hidden) start();
          else stop();
        },
        { threshold: 0 }
      );
      observer.observe(canvas.parentElement);
    }

    const onVisibility = () => {
      if (document.hidden) stop();
      else if (inView) start();
    };
    document.addEventListener("visibilitychange", onVisibility);

    let resizeObserver = null;
    if (typeof ResizeObserver !== "undefined") {
      resizeObserver = new ResizeObserver(() => {
        resize();
        if (prefersReduced) render(false);
      });
      resizeObserver.observe(canvas.parentElement);
    } else {
      window.addEventListener("resize", resize);
    }

    return () => {
      stop();
      if (observer) observer.disconnect();
      if (resizeObserver) resizeObserver.disconnect();
      else window.removeEventListener("resize", resize);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      aria-hidden="true"
      className={`pointer-events-none absolute inset-0 h-full w-full ${className}`.trim()}
    />
  );
}
